import type { IngredientCard } from '../types/game'

/**
 * I'd Eat That — the canonical 105 ingredient cards (deck position 001–105
 * in `public/games/iet/`). Cards live as PNGs at the same numbering: e.g.
 * id `butter` is `card-001.png`. The PNGs ship with print bleed; the
 * `IngredientCardImage` component crops it on render.
 *
 * "Process" cards (Fermented, Frozen, Steamed, …) carry a small white
 * dot indicator on the artwork — they're verbs/states a player can layer
 * onto an ingredient when writing a course, rather than ingredients in
 * their own right. They sit in the same shared deck so a hand naturally
 * mixes both. We mark them with `category: 'method'` so the engine /
 * AI can treat them differently if it wants to (e.g. nudge the AI to
 * apply them to a sibling card instead of using them solo).
 *
 * Card 106 is the deck back ("I'd Eat That / それ食べたい") and isn't part
 * of the deal pool.
 */

const CARD_PATH = '/games/iet/'
function cardImage(n: number): string {
  return `${CARD_PATH}card-${String(n).padStart(3, '0')}.webp`
}

/** Path to the deck back PNG for face-down rendering. */
export const INGREDIENT_BACK = cardImage(106)

/** Resolve the front art for a given ingredient id. */
export function ingredientImage(id: string): string {
  const card = INGREDIENT_BY_ID[id]
  return card ? cardImage(card.cardNumber) : INGREDIENT_BACK
}

/**
 * Extended IngredientCard — `cardNumber` lets us resolve the print-file
 * art, `isProcess` is a convenience flag for the method/preparation
 * cards that carry the white-dot indicator on the print.
 */
export interface IngredientCardWithArt extends IngredientCard {
  cardNumber: number
  isProcess: boolean
}

function card(
  cardNumber: number,
  id: string,
  name: string,
  color: string,
  category: IngredientCard['category'],
): IngredientCardWithArt {
  return { id, name, color, category, cardNumber, isProcess: category === 'method' }
}

// 105 cards transcribed from the print files in /public/games/iet/.
// Process cards (white-dot indicator) are interleaved at their natural
// colour-ramp position, not grouped at the end, so a deal stays
// chromatically coherent.
export const INGREDIENT_CARDS: IngredientCardWithArt[] = [
  // Yellows
  card(  1, 'butter',           'Butter',          '#E8E14A', 'dairy'),
  card(  2, 'eggs',             'Eggs',            '#E8E14A', 'protein'),
  card(  3, 'banana',           'Banana',          '#EEE54A', 'fruit'),
  card(  4, 'french_fries',     'French Fries',    '#F2EA38', 'grain'),
  // Olive / yellow-green
  card(  5, 'wheat',            'Wheat',           '#B8B028', 'grain'),
  card(  6, 'toast',            'Toast',           '#A89C24', 'grain'),
  card(  7, 'fermented',        'Fermented',       '#888024', 'method'),
  // Oranges
  card(  8, 'drink_mix',        'Drink Mix',       '#F09A4C', 'condiment'),
  card(  9, 'masala',           'Masala',          '#F08820', 'spice'),
  card( 10, 'pumpkin',          'Pumpkin',         '#E8581A', 'vegetable'),
  card( 11, 'blood_orange',     'Blood Orange',    '#E04A1C', 'fruit'),
  card( 12, 'ginger',           'Ginger',          '#B83820', 'spice'),
  card( 13, 'cinnamon',         'Cinnamon',        '#9C3018', 'spice'),
  card( 14, 'liver',            'Liver',           '#5C2810', 'protein'),
  // Pinks / reds
  card( 15, 'lychee',           'Lychee',          '#F2A6BE', 'fruit'),
  card( 16, 'farmed_salmon',    'Farmed Salmon',   '#EA6D88', 'protein'),
  card( 17, 'apricot',          'Apricot',         '#E84358', 'fruit'),
  card( 18, 'kimchi',           'Kimchi',          '#E62F38', 'vegetable'),
  card( 19, 'apple',            'Apple',           '#E22A2A', 'fruit'),
  card( 20, 'azuki',            'Azuki',           '#B83030', 'protein'),
  card( 21, 'miso',             'Miso',            '#8C2828', 'condiment'),
  // Purples
  card( 22, 'lavender',         'Lavender',        '#B7AAD9', 'spice'),
  card( 23, 'forgotten',        'Forgotten',       '#9888C8', 'method'),
  card( 24, 'fish_flakes',      'Fish Flakes',     '#7B68B8', 'protein'),
  // Deep blues
  card( 25, 'grapes',           'Grapes',          '#252562', 'fruit'),
  card( 26, 'blueberry',        'Blueberry',       '#1E2058', 'fruit'),
  card( 27, 'beets',            'Beets',           '#1E2058', 'vegetable'),
  card( 28, 'wine',             'Wine',            '#1A1828', 'condiment'),
  // Cyans / blues
  card( 29, 'ice_cream',        'Ice Cream',       '#A8DFE3', 'dairy'),
  card( 30, 'frozen',           'Frozen',          '#5BC4DE', 'method'),
  card( 31, 'water',            'Water',           '#26AEDF', 'special'),
  card( 32, 'liquid_nitrogen',  'Liquid Nitrogen', '#0F86C6', 'method'),
  card( 33, 'blue_spirulina',   'Blue Spirulina',  '#1B6CA8', 'special'),
  card( 34, 'marinated',        'Marinated',       '#155A85', 'method'),
  card( 35, 'ghost_pepper',     'Ghost Pepper',    '#11364A', 'spice'),
  // Light yellow-greens
  card( 36, 'hummus',           'Hummus',          '#E0E282', 'condiment'),
  card( 37, 'raw',              'Raw',             '#D8DC72', 'method'),
  card( 38, 'custard',          'Custard',         '#E0E064', 'dairy'),
  card( 39, 'pineapple',        'Pineapple',       '#E8E230', 'fruit'),
  card( 40, 'corn',             'Corn',            '#D6CE26', 'vegetable'),
  card( 41, 'potatoes',         'Potatoes',        '#BDC020', 'vegetable'),
  card( 42, 'mushrooms',        'Mushrooms',       '#A8A028', 'vegetable'),
  // Mints / pale greens
  card( 43, 'rosemary',         'Rosemary',        '#9DCFA0', 'spice'),
  card( 44, 'dill',             'Dill',            '#9CD0A2', 'spice'),
  card( 45, 'mint',             'Mint',            '#77C589', 'spice'),
  // Greens
  card( 46, 'cucumber',         'Cucumber',        '#1C9F45', 'vegetable'),
  card( 47, 'broccoli',         'Broccoli',        '#1A923E', 'vegetable'),
  card( 48, 'peas',             'Peas',            '#198434', 'vegetable'),
  card( 49, 'spinach',          'Spinach',         '#1F4D2A', 'vegetable'),
  // Limes
  card( 50, 'green_noodles',    'Green Noodles',   '#CFE044', 'grain'),
  card( 51, 'celery',           'Celery',          '#C2D830', 'vegetable'),
  card( 52, 'lime',             'Lime',            '#B6CE2A', 'fruit'),
  card( 53, 'cilantro',         'Cilantro',        '#A6C228', 'spice'),
  card( 54, 'lettuce',          'Lettuce',         '#92AC2C', 'vegetable'),
  card( 55, 'olives',           'Olives',          '#7A8A30', 'vegetable'),
  card( 56, 'old',              'Old',             '#6E7228', 'method'),
  // Tans / beiges
  card( 57, 'day_old_porridge', 'Day-Old Porridge','#D8D08A', 'grain'),
  card( 58, 'mayonnaise',       'Mayonnaise',      '#D2C880', 'condiment'),
  card( 59, 'clams',            'Clams',           '#BCB070', 'protein'),
  card( 60, 'parmesan',         'Parmesan',        '#A89C50', 'dairy'),
  // Olive-golds (process cluster)
  card( 61, 'deep_fried',       'Deep Fried',      '#998838', 'method'),
  card( 62, 'baked',            'Baked',           '#8E7E2C', 'method'),
  card( 63, 'barbequed',        'Barbequed',       '#5C4422', 'method'),
  // Greys / sages
  card( 64, 'tofu',             'Tofu',            '#CFC9B0', 'protein'),
  card( 65, 'goats_cheese',     'Goats Cheese',    '#B0AB90', 'dairy'),
  card( 66, 'sauerkraut',       'Sauerkraut',      '#9C9870', 'vegetable'),
  card( 67, 'jelly',            'Jelly',           '#A0A88E', 'condiment'),
  card( 68, 'anchovy',          'Anchovy',         '#838E7E', 'protein'),
  card( 69, 'licorice',         'Licorice',        '#7E807A', 'condiment'),
  card( 70, 'pepper',           'Pepper',          '#3E4248', 'spice'),
  // Reds again
  card( 71, 'rhubarb',          'Rhubarb',         '#D8332C', 'vegetable'),
  card( 72, 'lingonberry',      'Lingonberry',     '#C82424', 'fruit'),
  card( 73, 'tomato',           'Tomato',          '#A82020', 'vegetable'),
  card( 74, 'heart',            'Heart',           '#962323', 'protein'),
  card( 75, 'molasses',         'Molasses',        '#7B2424', 'condiment'),
  card( 76, 'coffee',           'Coffee',          '#682424', 'special'),
  card( 77, 'chocolate',        'Chocolate',       '#5A2222', 'condiment'),
  // Greens cluster
  card( 78, 'jalepenos',        'Jalepenos',       '#3A8E4A', 'spice'),
  card( 79, 'watercress',       'Watercress',      '#28823A', 'vegetable'),
  card( 80, 'pine_needles',     'Pine Needles',    '#1F7A38', 'spice'),
  card( 81, 'pickles',          'Pickles',         '#1C6A36', 'vegetable'),
  card( 82, 'artichoke',        'Artichoke',       '#205A30', 'vegetable'),
  card( 83, 'wheatgrass',       'Wheatgrass',      '#264C2A', 'special'),
  card( 84, 'matcha',           'Matcha',          '#244A30', 'special'),
  // Mustards / dark olives
  card( 85, 'cornbread',        'Cornbread',       '#B6A82C', 'grain'),
  card( 86, 'yeast',            'Yeast',           '#B89A1C', 'special'),
  card( 87, 'honey',            'Honey',           '#B89A1C', 'condiment'),
  card( 88, 'wild_garlic',      'Wild Garlic',     '#9C8420', 'spice'),
  card( 89, 'stale_bread',      'Stale Bread',     '#7E6E1E', 'grain'),
  card( 90, 'yesterdays',       "Yesterday's",     '#766820', 'method'),
  card( 91, 'sesame',           'Sesame',          '#5C5220', 'spice'),
  // Pinks / magentas
  card( 92, 'rose',             'Rose',            '#F4C0CE', 'spice'),
  card( 93, 'grapefruit',       'Grapefruit',      '#E14080', 'fruit'),
  card( 94, 'candyfloss',       'Candyfloss',      '#DC2A86', 'special'),
  card( 95, 'blow_torched',     'Blow Torched',    '#B22454', 'method'),
  card( 96, 'raspberry',        'Raspberry',       '#A21A36', 'fruit'),
  card( 97, 'cherries',         'Cherries',        '#931A2C', 'fruit'),
  card( 98, 'tree_bark',        'Tree Bark',       '#5C2030', 'special'),
  // Whites / greys / blacks
  card( 99, 'yoghurt',          'Yoghurt',         '#EFEFEF', 'dairy'),
  card(100, 'heavy_cream',      'Heavy Cream',     '#E0E0E0', 'dairy'),
  card(101, 'mochi',            'Mochi',           '#C2C2C2', 'grain'),
  card(102, 'steamed',          'Steamed',         '#9A9A9A', 'method'),
  card(103, 'salty',            'Salty',           '#7E7E7E', 'method'),
  card(104, 'coal_fired',       'Coal Fired',      '#4A4A4A', 'method'),
  card(105, 'burnt',            'Burnt',           '#161616', 'method'),
]

/** Quick lookup by id. */
export const INGREDIENT_BY_ID: Record<string, IngredientCardWithArt> =
  Object.fromEntries(INGREDIENT_CARDS.map(c => [c.id, c]))

/** Backwards-compatible accessor used by older screens. */
export function getCardById(id: string): IngredientCardWithArt | undefined {
  return INGREDIENT_BY_ID[id]
}

/** Turn an ingredient id into a human-readable name ("blood_orange" → "Blood Orange"). */
export function prettifyIngredientId(id: string): string {
  return id.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * Resolve an id to a card, synthesising a labelled placeholder for ids
 * that aren't in the deck. Weekly events can draw from a wider pool than
 * the printed deck (e.g. art-less seasonal ingredients), and those ids
 * must still render as a named card — not silently vanish. The
 * placeholder uses cardNumber 0, which `ingredientImage` maps to the
 * deck back, so it shows a back + the ingredient name.
 */
export function getCardOrPlaceholder(id: string): IngredientCardWithArt {
  return INGREDIENT_BY_ID[id] ?? {
    id,
    name: prettifyIngredientId(id),
    color: '#2A2A2A',
    category: 'special',
    cardNumber: 0,
    isProcess: false,
  }
}
